<template>
  <v-card outlined :loading="loading">
    <v-card-title>
      <div class="heading">Accountability Setup</div>
    </v-card-title>
    <v-card-text>

      <div class="mb-2">Setting up {{appName}} Accountability is easy and only takes a few minutes.</div>

      <div class="text-h5">Recommended Steps</div>

      <ol style="padding-left: 40px;">
        <li style="margin: 5px;">
          <completed-icon :completed="setupProgress.onboardingSurvey" style="margin-left: -45px;" />
          <span class="font-weight-bold" style="margin-left: 18px;">Take the onboarding survey.</span>
          <span class="pl-1 font-italic">(~1 minute)</span>
          <span v-if="!setupProgress.onboardingSurvey" class="font-italic error--text pl-2">(incomplete)</span>
          <br />
          <v-btn color="primary" small @click="showOnboardingSurvey = true">Take survey</v-btn>
        </li>
        <li style="margin: 5px;">
          <completed-icon :completed="setupProgress.addDevices" style="margin-left: -45px;" />
          <span class="font-weight-bold" style="margin-left: 18px;">Add devices to your account</span>
          <span class="pl-1 font-italic">(~3 minutes / device)</span>
          <span v-if="!setupProgress.addDevices" class="font-italic error--text pl-2">(incomplete)</span>
          <br />

          <v-btn color="primary" small @click="showAddDeviceDialog = true">Download app</v-btn>
          <div class="caption font-italic mt-1">
            Repeat for each device that needs to be monitored.
          </div>
          <add-device-dialog v-model="showAddDeviceDialog" />
        </li>
        <li style="margin: 5px;">
          <completed-icon :completed="setupProgress.addAccountabilityPartner" style="margin-left: -45px;" />
          <span class="font-weight-bold" style="margin-left: 18px;">Setup daily/weekly reports and alerts</span>
          <span class="pl-1 font-italic">(1 minute)</span>
          <span v-if="!setupProgress.addAccountabilityPartner" class="font-italic error--text pl-2">(incomplete)</span>
          <br />
          <v-btn color="primary" small @click="$router.push('/emails')" v-if="!seeHow.devices">
            Go to reports page
          </v-btn>
        </li>
        <li style="margin: 5px;">
          <completed-icon :completed="setupProgress.e2ee" style="margin-left: -45px;" />
          <span class="font-weight-bold" style="margin-left: 18px;">Enable end-to-end encryption</span>
          <span class="pl-1 font-italic">(2 minutes)</span>
          <span v-if="!setupProgress.e2ee" class="font-italic error--text pl-2">(incomplete)</span>
          <div>Enabling end-to-end encryption will increase the security of your accountability data.  Failure to enable will result in a $5 / month non-compliance fee.</div>
          <v-btn color="primary" small  @click="goToEnableE2EE" v-if="!seeHow.devices">
            Enable E2EE
          </v-btn>
        </li>
      </ol>

      <div class="font-italic">If you have any problems, please email <a :href="`mailto:support@${appDomain}`">support@{{appDomain}}</a>.</div>

<!--      <template v-if="setupProgress.minimumSet">-->
        <br />

        <div>
          <span class="text-h5 pr-2">Optional Steps</span>
          <v-btn text class="ma-0 pa-0 text--theme--primary font-italic" style="cursor: pointer;" @click="optionalShown = !optionalShown">{{(optionalShown ? 'hide' : 'show')}}</v-btn>
        </div>
      <v-expand-transition>
        <ul style="padding-left: 40px;" v-if="optionalShown">
          <li style="margin: 5px;">
            <completed-icon :completed="false" :optional="true" :indeterminate="true" style="margin-left: -45px;" />
            <span class="font-weight-bold" style="margin-left: 18px;">Adjust Device Settings</span>
            <span class="font-italic pl-1">(optional, 2 minutes)</span>
            <div>When devices are added to your account, they use default settings that work well under most circumstances.  However you can further customize those settings.</div>
            <v-btn text @click="$router.push('/devices')" v-if="!seeHow.devices">
              Go to device settings
            </v-btn>
          </li>
          <li style="margin: 5px;" v-if="product !== 'screenkin'">
            <completed-icon :completed="false" :optional="true" style="margin-left: -45px;" />
            <span class="font-weight-bold" style="margin-left: 18px;">Lock account</span>
            <span class="font-italic pl-1">(optional, 1 minute)</span>
            <div>If you wish, you can lock your account.  Locking will prevent changes from being made on your account.  In order to unlock, an unlock passcode will be sent to a report recipient that you must then enter in.</div>
            <v-btn text @click="$router.push('/account')" v-if="!seeHow.devices">
              Go to accounts page
            </v-btn>
          </li>
        </ul>
      </v-expand-transition>
<!--      </template>-->

    </v-card-text>

    <survey-onboarding v-model="showOnboardingSurvey" />
  </v-card>
</template>

<script>
  import SurveyOnboarding from "@/components/surveys/SurveyOnboarding";
  import CompletedIcon from "../CompletedIcon";
  import StoreConstants from "../../StoreConstants";
  import AddDeviceDialog from "@/components/devices/AddDeviceDialog";

  let unsubscribe = null

  export default {
    name: "SetupScreenshotAccountability",
    components: {AddDeviceDialog, CompletedIcon, SurveyOnboarding},
    data() {
      return {
        product: process.env.VUE_APP_PRODUCT_ID,
        appDomain: process.env.VUE_APP_DOMAIN,
        appName: process.env.VUE_APP_NAME,
        showAddDeviceDialog: false,
        showOnboardingSurvey: false,
        loading: true,
        optionalShown: false,
        seeHow: {
          devices: false
        },
        setupProgress: {
          addDevices: false,
          addAccountabilityPartner: false,
          e2ee: false
        }
      }
    },
    beforeMount() {
      unsubscribe = this.$store.subscribe((mutation, state) => {
        if(mutation.type === StoreConstants.commits.setAccount) {
          if(this.$store.state.account) {
            if(this.$store.state.account.account.setupProgress) {
              this.setupProgress = this.$store.state.account.account.setupProgress
              this.optionalShown = this.setupProgress.minimumSet
            }
          }
        }
      })

      this.loadAccountProgress()

      console.log('beforeMount')

      if(this.$store.state.account && this.$store.state.account.account.setupProgress && (Date.now() - this.$store.state.account.account.setupProgress.lastSet > 5000)) {
        this.$store.dispatch(StoreConstants.actions.loadAccount)
      }
    },
    beforeDestroy() {
      if(unsubscribe) {
        unsubscribe()
      }
    },
    methods: {
      async goToEnableE2EE() {

        if(!this.$store.getters.isSubscribed) {
          await this.$swal('Please subscribe in order to enable end-to-end encryption.')
          return
        }

        if(this.$store.state.inApp) {
          this.$router.push({
            path: 'inAppAccountHack',
            query: { forceEnableE2ee: true }
          })
        } else {
          this.$router.push({
            path: 'account',
            query: { forceEnableE2ee: true }
          })
        }
      },
      async loadAccountProgress() {
        this.loading = true
        await this.$store.state.accountLoadedPromise
        if(this.$store.state.account) {
          if(this.$store.state.account.account.setupProgress) {
            this.setupProgress = this.$store.state.account.account.setupProgress
            this.optionalShown = this.setupProgress.minimumSet
          }
        }
        this.loading = false
      }
    }
  }
</script>

<style scoped lang="scss">
  .image-border {
    border: 2px solid $theme-accent;
  }
</style>
